import React, { useEffect, useRef, useState } from "react";
import { 
  FaUserShield, FaEye, FaHandshake, FaLightbulb, 
  FaCheckDouble, FaClock, FaUsers, FaSearchPlus
} from "react-icons/fa";

const coreValues = [
  {
    letter: "A",
    title: "Accountability",
    desc: "We take full responsibility and accountability for our professional work.",
    icon: <FaUserShield />,
    side: "left"
  },
  {
    letter: "T",
    title: "Transparency & Timeliness",
    desc: "Committed to absolute transparency and strict confidentiality, we deliver high-quality professional services with a sharp focus on meeting every deadline.",
    icon: <FaClock />,
    side: "right"
  },
  {
    letter: "R",
    title: "Responsiveness",
    desc: "We value our clients' inquiries and provide prompt responses to every concern.",
    icon: <FaHandshake />,
    side: "left"
  },
  {
    letter: "I",
    title: "Integrity & Innovation",
    desc: "We maintain honest and transparency in all aspects of our business conduct while leveraging innovative approaches to solve problems and elevate our service quality.",
    icon: <FaLightbulb />,
    side: "right"
  },
  {
    letter: "C",
    title: "Commitment & Competency",
    desc: "We are dedicated to our work and only accept assignments within our competence.",
    icon: <FaCheckDouble />,
    side: "left"
  },
  {
    letter: <FaUsers />,
    title: "Caring for Staff & Community",
    desc: "Our success is rooted in stakeholder partnership, upholding responsible tax practices, environmental stewardship, and community support.",
    icon: <FaUsers />,
    side: "right"
  }
];

const ATRICPath = () => {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + window.innerHeight / 2;
      const elements = document.querySelectorAll(".atric-step");
      elements.forEach((el, idx) => {
        if (el.offsetTop < scrollPos) setActiveStep(idx);
      });
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="atric-path-section py-5" ref={sectionRef}>
      <div className="container py-5">
        
        {/* HEADER */}
        <div className="text-center mb-5 pb-5">
          <h1 className="text-light font-bold text-center">Our Core Values</h1>
          <h2 className="atric-path-title"><span className="gradient-text">GUIDING PRINCIPLES</span></h2>
          <p className="atric-path-lead mx-auto">
            As an audit firm, we uphold a set of core values that guide our professional conduct 
             and define how we serve our business community.
          </p>
        </div>

        {/* THE PATH */}
        <div className="atric-timeline-container">
          <div className="atric-central-line">
            <div 
              className="atric-line-filler" 
              style={{ height: `${(activeStep / (coreValues.length - 1)) * 100}%` }}
            ></div>
          </div>

          {coreValues.map((val, idx) => (
            <div 
              key={idx} 
              className={`atric-step ${val.side} ${idx <= activeStep ? "is-active" : ""}`}
            >
              {/* Central Node */}
              <div className="atric-node">
                <span className="node-letter">{val.letter}</span>
              </div>

              {/* Content Panel */}
              <div className="atric-panel-wrapper">
                <div className="atric-glass-panel">
                  <div className="panel-header d-flex align-items-center gap-3 mb-3">
                    <div className="panel-icon flex-shrink-0">{val.icon}</div>
                    <h4 className="panel-title mb-0">{val.title}</h4>
                  </div>
                  <p className="panel-desc mb-0">{val.desc}</p>
                  <div className="panel-decorative-letter">{val.letter}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>

      <style jsx>{`
        .atric-path-section {
          background: #0f172a;
          color: #fff;
          overflow: hidden;
          font-family: 'Inter', sans-serif;
        }

        .atric-path-title {
          font-weight: 900;
          font-size: clamp(2rem, 5vw, 3.5rem);
          letter-spacing: -1px;
        }

        .gradient-text {
          background: linear-gradient(to bottom right, #fff, #38bdf8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .atric-path-lead {
          max-width: 600px;
          color: #94a3b8;
          font-size: 1.1rem;
        }

        .atric-timeline-container {
          position: relative;
          max-width: 1000px;
          margin: 0 auto;
        }

        .atric-central-line {
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 4px;
          background: rgba(255, 255, 255, 0.1);
          transform: translateX(-50%);
          border-radius: 10px;
        }

        .atric-line-filler {
          width: 100%;
          background: linear-gradient(to bottom, #38bdf8, #0284c7);
          transition: height 0.6s ease;
          box-shadow: 0 0 15px #38bdf8;
        }

        .atric-step {
          position: relative;
          margin-bottom: 80px;
          width: 100%;
          display: flex;
          align-items: center;
          opacity: 0.3;
          transition: 0.5s ease;
        }

        .atric-step.is-active { opacity: 1; }

        .atric-node {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 50px;
          height: 50px;
          background: #1e293b;
          border: 4px solid rgba(255,255,255,0.1);
          border-radius: 50%;
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .atric-step.is-active .atric-node {
          border-color: #38bdf8;
          background: #38bdf8;
          transform: translateX(-50%) scale(1.2);
          box-shadow: 0 0 20px rgba(56, 189, 248, 0.5);
        }

        .node-letter {
          font-weight: 900;
          color: #fff;
          font-size: 1rem;
        }

        .atric-panel-wrapper {
          width: 50%;
          padding: 0 50px;
        }

        .atric-step.right { flex-direction: row-reverse; }

        .atric-glass-panel {
          background: rgba(30, 41, 59, 0.7);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 30px;
          border-radius: 24px;
          position: relative;
          overflow: hidden;
          transition: 0.4s;
        }

        .atric-step.is-active .atric-glass-panel {
          border-color: rgba(56, 189, 248, 0.4);
          background: rgba(30, 41, 59, 0.9);
        }

        .panel-icon {
          width: 45px;
          height: 45px;
          background: rgba(56, 189, 248, 0.1);
          color: #38bdf8;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
        }

        .panel-title {
          font-weight: 800;
          color: #fff;
          font-size: 1.4rem;
        }

        .panel-desc {
          color: #94a3b8;
          font-size: 0.95rem;
          line-height: 1.7;
          position: relative;
          z-index: 2;
        }

        .panel-decorative-letter {
          position: absolute;
          right: 5px;
          bottom: -20px;
          font-size: 8rem;
          font-weight: 900;
          color: rgba(255, 255, 255, 0.03);
          z-index: 1;
          user-select: none;
        }

        @media (max-width: 768px) {
          .atric-central-line { left: 20px; transform: none; }
          .atric-node { left: 20px; transform: none; }
          .atric-panel-wrapper { width: 100%; padding: 0 0 0 60px; }
          .atric-step.right { flex-direction: row; }
          .atric-step.is-active .atric-node { transform: scale(1.1); }
          .atric-path-title { font-size: 1.8rem; }
        }
      `}</style>
    </section>
  );
};

export default ATRICPath;